import { useEffect, useState, useCallback, useMemo, useRef, useContext } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { DataGrid,GridToolbarColumnsButton,GridToolbarExportContainer,GridToolbarQuickFilter,} from "@mui/x-data-grid";
import { SystemUpdateAltOutlined } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

import { callPostApi } from "../../function/ApiCallFunction";
import GradientLoader from "../../components/CommanComponents/GradientLoader";
import CustomFooterWithLoadMore from "../../include/footerWithLoadMore";
import { DataContext } from "../../context/DataContext";

const RedeemedCustHistory = () => 
{
    const navigate = useNavigate();
    const location = useLocation();
    const { lotCustName, lotCustId } = location.state || {};

    const {loginData }  = useContext(DataContext);
    const store_id      = loginData?.store_info?.store_id; 

    const [allData,         setAllData]        = useState([]);
    const [loading,         setLoading]        = useState(false);
    const [pageIndex,       setPageIndex]      = useState(0);
    const [hasMoreData,     setHasMoreData]    = useState(true);
    const [filtersApplied,  setFiltersApplied] = useState(false);
    const [filtersLoaded,   setFiltersLoaded]  = useState(false);
    const [totalAmt,        setTotalAmt]       = useState(0);

    const isInitialSessionLoad = useRef(false);
    const hasFetchedRef = useRef(false);

    /** Default filters */
    const defaultFilters = useMemo(() => ({store_id,customer_id: lotCustId,fetchLimit: "10",pageSize: "10",}),[store_id, lotCustId]);
    const [filters, setFilters] = useState(defaultFilters);

   /* ===================== RESET ===================== */
  const resetDataStates = () => 
  {
      setAllData([]);
      setPageIndex(0);
      setHasMoreData(true);
      setLoading(false);
  };

  /* ===================== APPLY FILTER ===================== */
  const applyFilters = () => 
  {
      setAllData([]);
      setPageIndex(0);
      setHasMoreData(true);
      setFiltersApplied(true);
  };

  /* ===================== CLEAR FILTER ===================== */
  const clearFilters = () => 
  {
      setFilters(defaultFilters);
      setAllData([]);
      setPageIndex(0);
      setHasMoreData(true);
      sessionStorage.setItem('redeemedHistoryFilter', JSON.stringify(defaultFilters));
      setFiltersApplied(true);
  };

  /* ===================== LOAD FROM SESSION ===================== */
  useEffect(() => 
  {
    const storedState = sessionStorage.getItem("redeemedHistoryFilter");
    if (storedState) 
    {
        try 
        {
            const parsedState = JSON.parse(storedState);
            setFilters({ ...defaultFilters, ...parsedState });
            isInitialSessionLoad.current = true;
            setFiltersApplied(true);
        } 
        catch { sessionStorage.removeItem("redeemedHistoryFilter"); }
    }
  }, [defaultFilters]);

  /* ===================== FETCH API ===================== */
  const fetchData = useCallback(async (page = pageIndex) => 
  {
      if (!filtersLoaded || loading || !hasMoreData) return;

      setLoading(true);
      try 
      {
          const StartNum = page * Number(filters.fetchLimit);
          const payload = {mod: "CASHI_CUST_QRCODE_LIST",data_arr: {...filters,start: StartNum,limit: filters.fetchLimit,}};
          
          const response = await callPostApi("cashi-qrcode", payload);

          if (response.status === "200" && response.data) 
          {
              const apiResponse = response.data;

              if (apiResponse.length > 0) 
              {
                  setAllData(prev => [...prev, ...apiResponse]);
                  setPageIndex(page);

                  const sum = apiResponse.reduce((t, i) => t + Number(i.redeem_amount || 0), 0);
                  setTotalAmt((prev) => prev + sum);
              } 
              else {  setHasMoreData(false); }

              if (apiResponse.length < filters.fetchLimit) { setHasMoreData(false); }

          } else { setHasMoreData(false); }
      } 
      catch { setHasMoreData(false); } 
      finally { setLoading(false); }

    },[filters, filtersLoaded, loading, hasMoreData, pageIndex]);
  
  /* ===================== INITIAL LOAD ===================== */  
  // Initial fetch trigger
  useEffect(() => {
      if (!hasFetchedRef.current && filtersLoaded) {
          hasFetchedRef.current = true;
          fetchData();
      }
  }, [filtersLoaded, fetchData]);

  // Save to sessionStorage
  useEffect(() => {
      if (filtersApplied) 
      {
          sessionStorage.setItem('redeemedFilter', JSON.stringify(filters));
          if (!isInitialSessionLoad.current) { fetchData(0); }
          isInitialSessionLoad.current = false;
          setFiltersApplied(false);
      }
  }, [filtersApplied, filters, fetchData]);

  // Set filters loaded once
  useEffect(() => { if (!filtersLoaded) setFiltersLoaded(true); }, [filtersLoaded]);
  
  /** Custom Toolbar */
  const CustomToolbar = () => (
    <GridToolbarExportContainer sx={{ justifyContent: "space-between", px: 2, py: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarQuickFilter debounceMs={300} />
    </GridToolbarExportContainer>
  );

  const fetchLimits    = [ "5", "10", "20", "50", "100", "250", "500", "1000"];

  return (
    <Container sx={{ my: 2 }}>
      
      {/* Top Buttons */}
      <Box sx={{ display: "flex", mb: 2 }}>
          <Button variant="contained" sx={{ background: "#1976d2" }} onClick={() => navigate(-1)} > Back</Button>
          <Button sx={{ml: "auto",background: "linear-gradient(195deg, #42424a, #191919)",color: "#fff",}}onClick={clearFilters}>Clear Filters</Button>
      </Box>

      {/* Title */}
      <Box sx={{ background: "linear-gradient(195deg, #49a3f1, #1A73E8)",p: 1,color: "#fff",borderRadius: "4px",mb: 3,
          display: "flex", justifyContent: "space-between",alignItems: "center",}}  > 
          <Typography sx={{ display: "flex", alignItems: "center" }}> <SystemUpdateAltOutlined sx={{ mr: 1 }} />Redeemed QR History – {lotCustName}</Typography>
          <Typography sx={{ fontWeight: 500 }}>Total Disbursed: ₹{totalAmt}</Typography>
      </Box>

      {/* Filters */}
      <div className="row g-2 mb-3">
          
          {/* Page Size Filter */}  
          <div className="col-12 col-md-2">
            <select className="form-control" value={filters.fetchLimit} onChange={(e) => setFilters(prev => ({ ...prev, fetchLimit: e.target.value }))} >
                <option value="">Record Fetch Limit</option>
                {fetchLimits.map(value => ( <option key={value} value={value}>{value}</option>))}
            </select>
        </div>  

          {/* Apply flter */}
          <div className="col-12 col-md-2 ms-auto">                                        
              <Button  sx={{background:'linear-gradient(195deg, #49a3f1, #1A73E8)', color:'#ffffff',ml:1,
                  boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px' }}
                      style={{float: 'right'}}  onClick={() => { resetDataStates(); applyFilters(); }} >Apply Filters</Button>
          </div>  
          
      </div>

      {/* DataGrid */}
      <Box sx={{ width: "100%" }}>
            <DataGrid rows={allData.map((row, i) => ({...row,srNo: i + 1 ,id: row.row_temp_id || `${pageIndex}-${i}`}))}
          columns={[
            { field: "srNo", headerName: "Sr No.", flex: 0.4 },
            { field: "qrcode_number", headerName: "QR Code", flex: 1.5 },
            { field: "redeem_amount", headerName: "Amount", flex: 0.6,},
            { field: "date_added", headerName: "Redeem Date", flex: 1,},
          ]}

            localeText={{ noRowsLabel: "No records found", noResultsOverlayLabel: "No matching data found", }}
            pageSize={Number(filters.pageSize)}
            onPageSizeChange={(newSize) => setFilters((prev) => ({ ...prev, pageSize: String(newSize) })) }
            pageSizeOptions={[10, 15, 20, 25, 40, 50, 80, 100]}
            pagination
            
            disableSelectionOnClick
            initialState={{pagination: {paginationModel: {pageSize: Number(filters.pageSize),page: 0,},},}}
            slots={{toolbar: CustomToolbar, footer: () => ( <CustomFooterWithLoadMore onLoadMore={() => fetchData(pageIndex + 1)} hasMoreData={hasMoreData} loading={loading} /> ), }}
            slotProps={{toolbar: {showQuickFilter: true,quickFilterProps: { debounceMs: 300 },},}}
            sx={{ '& .MuiDataGrid-cell': {fontSize: '13px', }, 
                '& .MuiDataGrid-columnHeaderTitle': {fontSize: '15px',fontWeight: 'bold', whiteSpace: 'normal !important', 
                        lineHeight: '1.5', textAlign: 'center',},
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {outline: "none"} }}
            rowHeight={38} headerHeight={45}
        />
      </Box>

      {loading && <GradientLoader text="Loading" />}
    </Container>
  );
};

export default RedeemedCustHistory;
